import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { PlusIcon } from "@heroicons/react/24/outline"
import { useGetCompanyArtifactsQuery } from "~/ui-rtk/api/companyArtifactApi"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { Button } from "~/ui-rtk/components/ui/controls"
import { H1 } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useAccess from "~/ui-rtk/hooks/access"
import useDate from "~/ui-rtk/hooks/date"
import PublishArtifactDialog from "./PublishArtifactDialog"

const dayjs = useDate()

/** Every artifact the agency published for this company. */
export default function ArtifactsListPage() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useAccess()
  const { data: artifacts, isLoading, error } = useGetCompanyArtifactsQuery()
  const [publishing, setPublishing] = useState(false)

  const open = (id: string) =>
    navigate(brandRoutes.ARTIFACTS.VIEW.replace(":id", id))

  return (
    <div className="px-system-md pb-system-2xl">
      <div className="mt-6 flex justify-between items-center gap-system-sm">
        <H1 className="text-white font-bold text-2xl">Artifacts</H1>
        {isSuperAdmin && (
          <Button
            size="sm"
            variant={{ variant: "solid", color: "pink" }}
            onClick={() => setPublishing(true)}
          >
            <span className="flex items-center gap-2">
              <PlusIcon className="w-4 h-4" />
              Publish artifact
            </span>
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="h-64 flex items-center justify-center">
          <Loader size={50} />
        </div>
      )}
      {error && (
        <div className="mt-10 text-system-gray-400">
          Could not load artifacts.
        </div>
      )}
      {artifacts && !artifacts.length && (
        <div className="mt-10 text-system-gray-400">
          No artifacts yet.
          {isSuperAdmin &&
            " Publish one here, or from Claude with the artifact_publish tool."}
        </div>
      )}

      <div className="mt-system-xl grid gap-system-md grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {(artifacts ?? []).map(artifact => (
          <button
            key={artifact.id}
            type="button"
            onClick={() => open(artifact.id)}
            className="text-left"
          >
            <Card
              rounded
              className="h-full p-5 bg-basic-dark-blue border border-basic-blue hover:border-system-blue-500 transition-colors"
            >
              <div className="text-white font-semibold">{artifact.title}</div>
              {artifact.description && (
                <p className="mt-1 text-sm text-system-gray-300 line-clamp-2">
                  {artifact.description}
                </p>
              )}
              <div className="mt-3 text-xs text-system-gray-500">
                {artifact.visibility === "PUBLIC" ? "Public" : "Private"} · v
                {artifact.currentVersion} · updated{" "}
                {dayjs(artifact.updatedAt).format("MMM D, YYYY")}
              </div>
            </Card>
          </button>
        ))}
      </div>

      {isSuperAdmin && (
        <PublishArtifactDialog
          isOpen={publishing}
          onClose={() => setPublishing(false)}
          onPublished={result => open(result.id)}
        />
      )}
    </div>
  )
}
